// fiscalCopy.js
//
// Copy y catálogos del wizard de facturación, con la TERMINOLOGÍA OFICIAL de cada
// ente (SII Chile / ARCA Argentina). Centralizado para mantener el wizard 100%
// alineado a la documentación oficial y no repetir strings. Las listas de
// prerrequisitos provienen de la investigación verificada del proceso de alta.

export const COUNTRY_OPTIONS = [
  { value: "CL", label: "Chile — SII" },
  { value: "AR", label: "Argentina — ARCA (ex-AFIP)" },
];

// Datos del ente por país, con los nombres oficiales de cada ambiente.
export const ENTITY = {
  CL: {
    name: "SII",
    full: "Servicio de Impuestos Internos (SII)",
    docName: "Documento Tributario Electrónico (DTE)",
    sandboxName: "Certificación (Maullín)",
    prodName: "Producción (Palena)",
    authName: "el SII",
  },
  AR: {
    name: "ARCA",
    full: "ARCA (ex-AFIP)",
    docName: "comprobante electrónico con CAE",
    sandboxName: "Homologación",
    prodName: "Producción",
    authName: "ARCA",
  },
};

export const entity = (country) => ENTITY[country] ?? ENTITY.CL;

export const envLabel = (country, environment) => {
  const e = entity(country);
  return environment === "production" ? e.prodName : e.sandboxName;
};

// El país NO lo elige el usuario: se determina por el país de operación del
// operador logístico. Mismo criterio que el selector de bancos de pagos.
const COUNTRY_NAME_TO_ISO = {
  chile: "CL",
  argentina: "AR",
  peru: "PE",
  "perú": "PE",
  colombia: "CO",
  venezuela: "VE",
  uruguay: "UY",
  mexico: "MX",
  "méxico": "MX",
};

/** Resuelve el país del operador a ISO alpha-2 (o null si no se puede). */
export function resolveOperatorCountry(company) {
  const raw =
    company?.country ||
    company?.operationsCountry ||
    company?.operationsAddresses?.[0]?.country;
  if (!raw) return null;
  const trimmed = String(raw).trim();
  if (trimmed.length === 2) return trimmed.toUpperCase();
  return COUNTRY_NAME_TO_ISO[trimmed.toLowerCase()] ?? null;
}

// Países con motor de facturación disponible hoy.
export const SUPPORTED_INVOICING_COUNTRIES = ["CL", "AR"];

// Nombre legible del país (para mensajes; soportados y no soportados).
export const COUNTRY_DISPLAY_NAME = {
  CL: "Chile",
  AR: "Argentina",
  PE: "Perú",
  CO: "Colombia",
  VE: "Venezuela",
  UY: "Uruguay",
  MX: "México",
};

// Checklist OFICIAL de prerrequisitos que el operador debe tener ANTES de empezar.
// Es una DECLARACIÓN del operador: el wizard no puede verificarlos ante el ente.
export const PREREQUISITES = {
  CL: [
    {
      id: "cl_inicio_actividades",
      label: "Inicio de Actividades vigente ante el SII (1ª Categoría).",
      required: true,
    },
    {
      id: "cl_certificado",
      label:
        "Certificado Digital vigente (de una entidad acreditada ante el SII), asociado al RUT del firmante.",
      required: true,
    },
    {
      id: "cl_postulacion",
      label:
        "Postulación al sistema de Facturación de Mercado ACEPTADA por el SII y Usuario Administrador designado.",
      required: true,
    },
    {
      id: "cl_caf",
      label:
        "Al menos un CAF (folios) descargado por cada tipo de documento que vas a emitir.",
      required: true,
    },
    {
      id: "cl_resolucion",
      label:
        "Para producción: certificación aprobada en Maullín y Resolución del SII que te autoriza. (No necesaria para pruebas.)",
      required: false,
    },
  ],
  AR: [
    {
      id: "ar_clave_fiscal",
      label: "CUIT activo y Clave Fiscal con Nivel de Seguridad 3.",
      required: true,
    },
    {
      id: "ar_condicion_iva",
      label:
        "Condición frente al IVA definida (Responsable Inscripto / Monotributo / Exento).",
      required: true,
    },
    {
      id: "ar_certificado",
      label:
        "Certificado digital (homologación vía WSASS / producción vía Administrador de Certificados Digitales) y autorización del certificado al servicio «wsfe».",
      required: true,
    },
    {
      id: "ar_punto_venta",
      label:
        "Punto de Venta de tipo «Web Services» dado de alta en ARCA, nuevo y exclusivo para este uso.",
      required: true,
    },
    {
      id: "ar_reloj",
      label:
        "Para producción: homologación completada y certificado/PV de producción operativos.",
      required: false,
    },
  ],
};

// Condición frente al IVA del EMISOR (NO es la forma societaria). Define la letra
// del comprobante en Argentina.
export const IVA_OPTIONS = {
  CL: [
    { value: "cl_afecto", label: "Afecto a IVA" },
    { value: "cl_exento", label: "Exento de IVA" },
  ],
  AR: [
    { value: "ar_responsable_inscripto", label: "Responsable Inscripto" },
    { value: "ar_monotributo", label: "Monotributo" },
    { value: "ar_exento", label: "Exento" },
  ],
};

// Tipos de documento por defecto (canónicos) por país, con su código oficial.
export const DOC_TYPE_OPTIONS = {
  CL: [
    { value: "invoice", label: "Factura electrónica (33)" },
    { value: "exempt_invoice", label: "Factura exenta (34)" },
    { value: "credit_note", label: "Nota de crédito (61)" },
  ],
  AR: [
    { value: "invoice", label: "Factura (A/B/C según tu condición IVA)" },
    { value: "credit_note", label: "Nota de crédito" },
  ],
};

// Tipos de CAF que se pueden cargar en Chile (v1).
export const CAF_DOC_TYPES = [
  { value: "33", label: "Factura electrónica (33)" },
  { value: "34", label: "Factura exenta (34)" },
  { value: "61", label: "Nota de crédito (61)" },
];

export const EMIT_TRIGGER_OPTIONS = [
  { value: "on_payment", label: "Al confirmarse el pago de la nota" },
  { value: "on_note", label: "Al crear la nota de cobro" },
];

export const TAX_ID_LABEL = { CL: "RUT", AR: "CUIT" };
export const TAX_RATE_HINT = { CL: 19, AR: 21 };
